import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, ListChecks, Check } from 'lucide-react';
import svgPaths from '../imports/svg-bottomnav';

export type OnboardingStep = 0 | 1 | 2 | 3 | 4;

interface OnboardingStepperProps {
  currentStep: OnboardingStep;
  onAction?: (step: OnboardingStep) => void;
}

// Coin Icon from Bottom Navigation
function CoinIcon({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg className={className} style={style} fill="none" viewBox="0 0 24 24">
      <path
        d={svgPaths.coinCircle}
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <path
        d={svgPaths.coinStar}
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
    </svg>
  );
}

// Step circle component with refined state design
interface StepCircleProps {
  stepNumber: 1 | 2 | 3;
  currentStep: OnboardingStep;
  icon: React.ReactNode;
}

function StepCircle({ stepNumber, currentStep, icon }: StepCircleProps) {
  const isCompleted = currentStep > stepNumber;
  const isCurrent = currentStep === stepNumber;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Circle */}
      <motion.div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',

          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: isCompleted
            ? 'white'
            : isCurrent
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(255, 255, 255, 0.15)',
          border: isCurrent ? '2px solid white' : 'none',
          boxShadow: isCurrent
            ? '0 0 0 3px rgba(255, 255, 255, 0.25)'
            : isCompleted
              ? '0 2px 8px rgba(0, 0, 0, 0.08)'
              : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        initial={false}
        animate={{
          scale: 1,
        }}
      >
        {isCompleted ? (
          <Check
            strokeWidth={2.5}
            style={{
              width: '16px',
              height: '16px',
              color: 'var(--kojo-blue)',
            }}
          />
        ) : (
          <div
            style={{
              color: isCurrent
                ? 'var(--kojo-blue)'
                : 'rgba(255, 255, 255, 0.6)',
              transition: 'color 0.3s ease',
            }}
          >
            {icon}
          </div>
        )}
      </motion.div>
    </div>
  );
}

// Connector line between steps with refined design
interface ConnectorLineProps {
  isActive: boolean;
}

function ConnectorLine({ isActive }: ConnectorLineProps) {
  return (
    <div
      style={{
        width: '2px',
        height: '16px',
        backgroundColor: isActive
          ? 'rgba(255, 255, 255, 0.6)'
          : 'rgba(255, 255, 255, 0.2)',
        borderRadius: '1px',
        transition: 'background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    />
  );
}

export function OnboardingStepper({
  currentStep,
  onAction,
}: OnboardingStepperProps) {
  // State 4 = Concluído = No banner shown
  if (currentStep === 4) {
    return null;
  }

  // State 0 = Verification Required (re-verification) - No stepper timeline
  if (currentStep === 0) {
    return (
      <motion.div
        key="verification-required"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1],
        }}
      >
        {/* Refined blue container with gradient */}
        <div
          style={{
            background:
              'linear-gradient(135deg, var(--kojo-blue) 0%, var(--kojo-blue-dark) 100%)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle overlay for depth */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1), transparent 60%)',
              pointerEvents: 'none',
            }}
          />

          {/* Content container - simpler layout without timeline */}
          <div
            style={{
              paddingLeft: '24px', // px-6
              paddingRight: '24px',
              paddingTop: '24px', // py-6
              paddingBottom: '24px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '16px', // gap-4
                alignItems: 'flex-start',
              }}
            >
              {/* Icon only (no timeline) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '2px solid white',
                  boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.25)',
                }}
              >
                <ShieldCheck
                  strokeWidth={2}
                  style={{
                    width: '20px', // w-5
                    height: '20px', // h-5
                    color: 'var(--kojo-blue)',
                  }}
                />
              </div>

              {/* Content */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* Title */}
                <h3
                  style={{
                    color: 'white',
                    fontSize: 'var(--text-lg)',
                    lineHeight: '1.35',
                    fontWeight: 'var(--font-weight-semibold)',
                    marginBottom: '6px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Faça nova verificação facial
                </h3>

                {/* Description */}
                <p
                  style={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: 'var(--text-sm)',
                    lineHeight: '1.6',
                    fontWeight: 'var(--font-weight-normal)',
                    marginBottom: '16px',
                  }}
                >
                  Necessário para continuar liberando pedidos com segurança.
                </p>

                {/* CTA Button */}
                <motion.button
                  onClick={() => {
                    alert(
                      'Reverificação facial iniciada! Verificação concluída.'
                    );
                    onAction?.(0);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 150ms ease',

                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--kojo-blue)',
                    backgroundColor: 'white',
                    paddingLeft: '18px',
                    paddingRight: '18px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    border: 'none',
                    borderRadius: 'var(--radius-button)',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                    letterSpacing: '-0.01em',
                  }}
                  whileHover={{
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }}
                  whileTap={{
                    transform: 'translateY(0px)',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                  }}
                  transition={{
                    duration: 0.15,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  aria-label="Começar verificação"
                  type="button"
                >
                  Começar verificação →
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  const stepConfig = {
    1: {
      icon: (
        <ShieldCheck
          style={{ width: '16px', height: '16px' }}
          strokeWidth={2}
        />
      ),
      stepLabel: 'ETAPA 1/3',
      title: 'Faça a verificação facial',
      description: 'Necessário para liberar pedidos com segurança.',
      ctaText: 'Começar verificação',
    },
    2: {
      icon: <CoinIcon style={{ width: '16px', height: '16px' }} />,
      stepLabel: 'ETAPA 2/3',
      title: 'Adquira moedas',
      description:
        'Você precisa de moedas para liberar pedidos e contactar clientes.',
      ctaText: 'Ver pacotes de moedas',
    },
    3: {
      icon: (
        <ListChecks style={{ width: '16px', height: '16px' }} strokeWidth={2} />
      ),
      stepLabel: 'ETAPA 3/3',
      title: 'Complete seu perfil',
      description:
        'Um perfil completo aumenta suas chances de fechar com clientes.',
      ctaText: 'Preencher perfil',
    },
  };

  const config = stepConfig[currentStep as 1 | 2 | 3];

  const handleStepAction = () => {
    let message = '';
    if (currentStep === 1) {
      message = 'Etapa 1/3 realizada! Verificação concluída.';
    } else if (currentStep === 2) {
      message = 'Etapa 2/3 realizada! Compra inicial de moedas concluída.';
    } else if (currentStep === 3) {
      message = 'Etapa 3/3 realizada! Perfil completo.';
    }

    alert(message);
    onAction?.(currentStep);
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1],
        }}
      >
        {/* Refined blue container with gradient */}
        <div
          style={{
            background:
              'linear-gradient(135deg, var(--kojo-blue) 0%, var(--kojo-blue-dark) 100%)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle overlay for depth */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1), transparent 60%)',
              pointerEvents: 'none',
            }}
          />

          {/* Content container with improved spacing */}
          <div
            style={{
              paddingLeft: '24px',
              paddingRight: '24px',
              paddingTop: '24px',
              paddingBottom: '24px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Two-column layout with refined spacing */}
            <div style={{ display: 'flex', gap: '32px' }}>
              {/* Left column: Vertical stepper indicator */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flexShrink: 0,
                  gap: '8px',
                  paddingTop: '2px',
                }}
              >
                <StepCircle
                  stepNumber={1}
                  currentStep={currentStep}
                  icon={
                    <ShieldCheck
                      style={{ width: '16px', height: '16px' }}
                      strokeWidth={2}
                    />
                  }
                />
                <ConnectorLine isActive={currentStep > 1} />
                <StepCircle
                  stepNumber={2}
                  currentStep={currentStep}
                  icon={<CoinIcon style={{ width: '16px', height: '16px' }} />}
                />
                <ConnectorLine isActive={currentStep > 2} />
                <StepCircle
                  stepNumber={3}
                  currentStep={currentStep}
                  icon={
                    <ListChecks
                      style={{ width: '16px', height: '16px' }}
                      strokeWidth={2}
                    />
                  }
                />
              </div>

              {/* Right column: Active step content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'rgba(255, 255, 255, 0.75)',
                    letterSpacing: '0.08em',
                    lineHeight: '1.6',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                  }}
                >
                  {config.stepLabel}
                </div>

                <h3
                  style={{
                    color: 'white',
                    fontSize: 'var(--text-lg)',
                    lineHeight: '1.35',
                    fontWeight: 'var(--font-weight-semibold)',
                    marginBottom: '6px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {config.title}
                </h3>

                <p
                  style={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: 'var(--text-sm)',
                    lineHeight: '1.6',
                    fontWeight: 'var(--font-weight-normal)',
                    marginBottom: '16px',
                  }}
                >
                  {config.description}
                </p>

                <motion.button
                  onClick={handleStepAction}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 150ms ease',

                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--kojo-blue)',
                    backgroundColor: 'white',
                    paddingLeft: '18px',
                    paddingRight: '18px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    border: 'none',
                    borderRadius: 'var(--radius-button)',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                    letterSpacing: '-0.01em',
                  }}
                  whileHover={{
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }}
                  whileTap={{
                    transform: 'translateY(0px)',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                  }}
                  transition={{
                    duration: 0.15,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  aria-label={config.ctaText}
                  type="button"
                >
                  {config.ctaText} →
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
